import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetCartQuery, useGetStorefrontQuery } from '../Api/Api';
import useStyles from '../styles';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import CartSummary from '../Components/Cart/CartSummary';
import CartItemsView from '../Components/Cart/CartItemsView';
import CAN, { ORDERS } from '../Components/Auth/Permissions';
import { parseCartPageConfig } from '../Components/Helpers/cartPageConfig';
import { elementStyleFrom } from '../Components/Helpers/searchPageConfig';
import { useLocalization } from '../Components/Helpers/useLocalization';

const CartPage: React.FC = () => {
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
    const cart = useGetCartQuery();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const error = queryParams.get('error');
    const styles = useStyles();
    const storefrontSettings = useGetStorefrontQuery();
    // Merchant cart-page styling (cartPageConfig JSON). The line-item / empty-state styling lives
    // in CartItemsView; here we only need the page container, title, and summary box.
    const cartConfig = React.useMemo(() => parseCartPageConfig(storefrontSettings.data?.cartPageConfig), [storefrontSettings.data?.cartPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(cartConfig.container), [cartConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(cartConfig.title, 'heading'), [cartConfig.title]);
    const summaryBoxStyle = React.useMemo(() => elementStyleFrom(cartConfig.summaryBox), [cartConfig.summaryBox]);
    const navigate = useNavigate();
    const L = useLocalization();

    const canPlaceOrder = CAN(ORDERS);
    if (!canPlaceOrder) {
        navigate('/');
    }

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isEmpty = (cart.data?.products?.length ?? 0) === 0 && (cart.data?.bundles?.length ?? 0) === 0;

    if (cart.isLoading) return <FullPageSpinner message={L('localization_common_loading')} />;

    return (
        <div className={styles.mainContent} style={containerStyle}>
            <h1 style={titleStyle}>{L('localization_cart_page_title')}</h1>
            {isEmpty ?
                <CartItemsView /> :
                <div style={{ display: 'flex', gap: '1em', alignItems: 'flex-start', flexDirection: windowWidth <= 700 ? 'column' : 'row' }}>
                    <CartItemsView layout='auto' />
                    <div className='cart-summary' style={summaryBoxStyle}>
                        <CartSummary error={error ?? undefined} cart={cart.data} />
                    </div>
                </div>
            }
        </div>
    );
};

export default CartPage;
